import { LUDOLIO_HOW_IT_WORKS_URL } from '../config/constants';

const PROTOCOL_TIMEOUT_MS = 2000;
const REDIRECT_PAINT_MS = 300;

export type PlayOnLudolioStatus = 'opening' | 'opened' | 'redirecting';

export function ludolioGameProtocolUrl(appId: number): string {
  return `ludolio://game/${appId}`;
}

export function isMobileDevice(): boolean {
  if (typeof navigator === 'undefined') return false;

  const uaData = navigator as Navigator & { userAgentData?: { mobile?: boolean } };
  if (uaData.userAgentData?.mobile) return true;

  const ua = navigator.userAgent;
  if (/Android|iPhone|iPod/i.test(ua)) return true;
  if (/iPad/i.test(ua)) return true;
  if (navigator.maxTouchPoints > 1 && /Mac/.test(ua)) return true;

  return false;
}

export function goToHowItWorks(): void {
  window.location.assign(LUDOLIO_HOW_IT_WORKS_URL);
}

let playAttemptInFlight = false;

function redirectToHowItWorks(onStatus?: (status: PlayOnLudolioStatus) => void): void {
  onStatus?.('redirecting');
  window.setTimeout(() => {
    goToHowItWorks();
  }, REDIRECT_PAINT_MS);
}

export function playOnLudolio(
  appId: number,
  onStatus?: (status: PlayOnLudolioStatus) => void,
): boolean {
  if (playAttemptInFlight) return false;

  if (!Number.isInteger(appId) || appId <= 0 || isMobileDevice()) {
    playAttemptInFlight = true;
    redirectToHowItWorks(onStatus);
    return true;
  }

  playAttemptInFlight = true;
  onStatus?.('opening');

  const protocolUrl = ludolioGameProtocolUrl(appId);
  let opened = false;
  let iframe: HTMLIFrameElement | null = null;

  const cleanup = () => {
    playAttemptInFlight = false;
    window.removeEventListener('blur', onLeave);
    document.removeEventListener('visibilitychange', onVisibility);
    iframe?.remove();
    iframe = null;
  };

  const onLeave = () => {
    opened = true;
    cleanup();
    onStatus?.('opened');
  };

  const onVisibility = () => {
    if (document.visibilityState === 'hidden') {
      opened = true;
      cleanup();
      onStatus?.('opened');
    }
  };

  window.addEventListener('blur', onLeave);
  document.addEventListener('visibilitychange', onVisibility);

  const isFirefox = /Firefox/i.test(navigator.userAgent);
  if (isFirefox) {
    iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = protocolUrl;
    document.body.appendChild(iframe);
  } else {
    window.location.href = protocolUrl;
  }

  window.setTimeout(() => {
    if (opened || document.hidden || !document.hasFocus()) {
      cleanup();
      return;
    }
    cleanup();
    redirectToHowItWorks(onStatus);
  }, PROTOCOL_TIMEOUT_MS);

  return true;
}
